import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useApi } from '@/lib/useApi';
import { ApiError } from '@/lib/api';
import { Logo } from '@/components/Shell';
import { Button, Field, Input } from '@/components/ui';

export default function StaffLogin() {
  const { signInStaff } = useAuth();
  const navigate = useNavigate();
  const { data: status } = useApi<{ needsSetup: boolean; signupEnabled: boolean }>('/auth/status');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInStaff(email.trim(), password);
      navigate('/app');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign you in');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel — hidden on small screens where it would just push the form down. */}
      <div className="hidden lg:flex mesh border-r border-line p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2.5 w-fit">
          <Logo />
          <span className="font-extrabold tracking-tight text-lg">PROVIA</span>
        </Link>
        <div>
          <h1 className="text-display max-w-md">Welcome back.</h1>
          <p className="text-muted mt-5 max-w-sm leading-relaxed">
            Verify today's payments, check who is overdue, and keep the portfolio moving.
          </p>
        </div>
        <p className="text-sm text-faint">Tenants sign in with their phone number instead.</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 text-sm text-muted mb-8 hover:text-ink">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>

          <h2 className="text-title">Manager sign in</h2>
          <p className="text-muted mt-2 text-sm">Use the email address your organisation set up.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="username"
                required
                autoFocus
              />
            </Field>

            <div className="space-y-1.5">
              {/*
                The link sits on the label row rather than under the button:
                somebody who has just failed to remember their password is
                looking at this field, not below the form.
              */}
              <div className="flex items-baseline justify-between gap-3">
                <label className="block text-sm font-medium text-ink">Password</label>
                <Link to="/forgot-password" className="link text-xs">Forgotten password?</Link>
              </div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-danger bg-danger/10
                              border border-danger/25 rounded-xl px-3 py-2.5">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
              Sign in
            </Button>
          </form>

          {/*
            Shown to everyone, not only on an empty deployment. This used to
            appear only when no organisation existed at all, which meant the
            second customer onwards had no route to signing up from the one
            screen they were most likely to land on.
          */}
          {status?.signupEnabled !== false && (
            <div className="mt-6 p-4 rounded-xl bg-brand/10 border border-brand/25">
              <p className="text-sm text-ink font-medium">No account yet?</p>
              <p className="text-sm text-muted mt-1">
                <Link to="/setup" className="link">Start a free trial</Link> — no card needed,
                or <Link to="/pricing" className="link">see pricing</Link> first.
              </p>
            </div>
          )}

          <p className="text-sm text-faint mt-8 text-center">
            Are you a tenant? <Link to="/tenant-login" className="link">Use the tenant portal</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
